/**
 * Relation-heat-map-canvas — gerendeerd voor view-kind
 * `analytics.relation-heat-map`.
 *
 * Verschil met `HeatMapCanvasV2` (objecten-heatmap):
 *  - Datapunten = relation-instances (één per matchend stereotype),
 *    niet concepten
 *  - Eén as gebruikt een attribuut van de relation ZELF (werkelijke
 *    deployment-waarde), de andere as gebruikt een attribuut van een
 *    verbonden concept met een ander stereotype (= "template" met
 *    theoretische waarde)
 *  - Diagonaal-referentielijn als visuele hint voor "werkelijk =
 *    template"
 *
 * Config-bron: diagram.attributes (analogu aan HeatMapCanvasV2).
 * Velden:
 *  - relation_stereotype: id van het relation-stereotype te plotten
 *  - relation_attribute: attribuut van die relation als X- of Y-as
 *  - template_stereotype: concept-stereotype dat de template-waarde
 *    levert (= aan de andere kant van de relatie)
 *  - template_attribute: attribuut van het template-concept
 *  - template_axis: "vertical" | "horizontal" (welke as de template
 *    bevat; de relation komt automatisch op de andere as)
 *  - show_diagonal: bool (toon diagonaal-referentielijn)
 */

import type { AttributeSchema, Concept, Diagram, Relation } from "@ae/domain";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { getRegistry, resolveBilingual } from "../../registry";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";
import { dotPositions } from "./HeatMapCanvasV2";

interface RelationHeatMapCanvasProps {
  diagram: Diagram;
}

type AxisMode = "enum" | "numeric" | "unconfigured";

interface AxisModel {
  mode: AxisMode;
  attrName: string;
  /** Enum-options of bucket-labels voor numeriek. */
  buckets: { label: string; min?: number; max?: number; enumValue?: string }[];
  enumOptions?: readonly string[];
}

/**
 * Intermediate per-relatie-record uit de data-extractie. Draagt zowel de
 * type-waarde (entity, op de relatie) als de instance-waarde (uit de
 * viewState-bag) zodat de plaatsings-stap er één of twee bolletjes van
 * kan maken (niveau Type → alleen type; niveau Instantie → beide).
 */
interface RelRecord {
  relation: Relation;
  templateConcept: Concept;
  number: number;
  tmplValue: unknown;
  /** Relatie-waarde op de entity (type-bolletje). */
  relValueType: unknown;
  /** Relatie-waarde uit de instance-bag (instance-bolletje); undefined = geen. */
  relValueInstance: unknown;
}

interface PlottedRelation {
  relation: Relation;
  templateConcept: Concept;
  /** Bucket-index voor verticale en horizontale as. -1 = onbekend. */
  vBucket: number;
  hBucket: number;
  /** 1-based volgnummer voor weergave in cel + legenda (gedeeld per paar). */
  number: number;
  /** "type" = entity-waarde-bolletje; "instance" = instance-waarde-bolletje. */
  kind: "type" | "instance";
}

export function RelationHeatMapCanvas({ diagram }: RelationHeatMapCanvasProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const revision = useWorkspaceData((s) => s.revision);

  const attrs = diagram.attributes ?? {};
  const relStereo = typeof attrs.relation_stereotype === "string" ? attrs.relation_stereotype : "";
  const relAttr = typeof attrs.relation_attribute === "string" ? attrs.relation_attribute : "";
  const tmplStereo = typeof attrs.template_stereotype === "string" ? attrs.template_stereotype : "";
  const tmplAttr = typeof attrs.template_attribute === "string" ? attrs.template_attribute : "";
  const templateAxis: "vertical" | "horizontal" =
    attrs.template_axis === "horizontal" ? "horizontal" : "vertical";
  const showDiagonal = attrs.show_diagonal !== false; // default aan
  // Niveau waarop waarden gelezen worden: "type" = entity-waarden op de
  // relatie/het concept; "instance" = per-diagram-instance-waarden.
  // Default "type".
  const dataLevel: "type" | "instance" = attrs.data_level === "instance" ? "instance" : "type";

  // Kleur-config — identiek aan HeatMapCanvasV2: één start- en één
  // eindkleur voor een diagonale cel-gradient (links-onder = start,
  // rechts-boven = eind) met een "Kleur toepassen"-toggle (default aan).
  const applyColor = attrs.apply_color !== false;
  const colorStart = typeof attrs.color_start === "string" ? attrs.color_start : "#dc2626";
  const colorEnd = typeof attrs.color_end === "string" ? attrs.color_end : "#16a34a";
  // Richting van het kleurverloop: horizontaal (links→rechts), verticaal
  // (onder→boven) of beide (diagonaal). Default beide.
  const colorDirection: "horizontal" | "vertical" | "both" =
    attrs.color_direction === "horizontal" || attrs.color_direction === "vertical"
      ? attrs.color_direction
      : "both";
  // Bolletje-kleuren — eigen pickers, los van de cel-gradient.
  const dotFillOverride =
    typeof attrs.dot_fill_color === "string" ? attrs.dot_fill_color : undefined;
  const dotTextOverride =
    typeof attrs.dot_text_color === "string" ? attrs.dot_text_color : undefined;
  // Tweede bolletje-kleur voor het instance-bolletje (niveau Instantie).
  // Default amber zodat 't visueel verschilt van het type-bolletje.
  const dotFillInstance =
    typeof attrs.dot_fill_color_instance === "string" ? attrs.dot_fill_color_instance : "#f59e0b";
  const dotTextInstance =
    typeof attrs.dot_text_color_instance === "string" ? attrs.dot_text_color_instance : undefined;

  // Zoom-state — identiek aan HeatMapCanvasV2: scale rond matrix-midden,
  // wheel + toolbar-knoppen. Toolbar verplaatsbaar over 4 hoeken.
  const [zoom, setZoom] = useState(1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const hovered = useRef(false);
  const [toolbarPos, setToolbarPos] = useState<
    "top-left" | "top-right" | "bottom-left" | "bottom-right"
  >("bottom-right");
  const toolbarPosClass: Record<typeof toolbarPos, string> = {
    "top-left": "top-3 left-3",
    "top-right": "top-3 right-3",
    "bottom-left": "bottom-3 left-3",
    "bottom-right": "bottom-3 right-3",
  };
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setZoom((z) => clamp(z * (e.deltaY < 0 ? 1.1 : 1 / 1.1), 0.3, 4));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);
  // Toetsenbord +/-/0 — consistent met de overige diagram-canvases (geen
  // modifier; editable-guard; alleen wanneer de muis boven de heat-map is).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!hovered.current || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = document.activeElement;
      if (
        t instanceof HTMLElement &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.tagName === "SELECT" ||
          t.isContentEditable)
      ) {
        return;
      }
      if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        setZoom((z) => clamp(z * 1.1, 0.3, 4));
      } else if (e.key === "-") {
        e.preventDefault();
        setZoom((z) => clamp(z / 1.1, 0.3, 4));
      } else if (e.key === "0") {
        e.preventDefault();
        setZoom(1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // ── Data extraction ─────────────────────────────────────────────
  const { records, relSchema, tmplSchema, hiddenCount } = useMemo(() => {
    void revision;
    if (!relStereo || !tmplStereo) {
      return {
        records: [] as RelRecord[],
        relSchema: undefined as AttributeSchema | undefined,
        tmplSchema: undefined as AttributeSchema | undefined,
        hiddenCount: 0,
      };
    }
    const reg = getRegistry();
    const store = useWorkspaceData.getState().store;
    const relStereoDef = reg.getStereotype(relStereo);
    // Template mag een concept-stereotype ÓF een gewoon ArchiMate-type
    // zijn. Eerst als stereotype proberen; anders als element-type.
    const tmplStereoDef = reg.getStereotype(tmplStereo);
    const tmplTypeDef = tmplStereoDef ? undefined : reg.getElementType(tmplStereo);
    const relSchemaResolved = relStereoDef
      ? (reg.resolveStereotypeAttributes(relStereoDef).find((a) => a.name === relAttr) ?? undefined)
      : undefined;
    const tmplAttrSchemas = tmplStereoDef
      ? reg.resolveStereotypeAttributes(tmplStereoDef)
      : (tmplTypeDef?.attributes ?? []);
    const tmplSchemaResolved = tmplAttrSchemas.find((a) => a.name === tmplAttr) ?? undefined;
    // Een concept matcht het template-(sub)type als het het stereotype
    // draagt OF van dat type is.
    const matchesTemplate = (c: Concept | undefined): boolean =>
      !!c && (c.stereotypes.includes(tmplStereo) || c.typeId === tmplStereo);

    // Voor data_level "instance": index per ref-id de eerste viewState-
    // entry-bag (flat Record<attrName, value>). Voor "type" lezen we de
    // entity-waarden (relation.attributes / concept.attributes).
    const relInstanceAttrs = new Map<string, Record<string, unknown>>();
    if (dataLevel === "instance") {
      for (const d of store.listDiagrams()) {
        for (const [key, entry] of Object.entries(d.viewState)) {
          if (entry.type !== "relation") continue;
          const refId = entry.ref ?? key;
          const bag = entry.stereotypeAttributes;
          if (!bag || typeof bag !== "object") continue;
          const flat = bag as Record<string, unknown>;
          const existing = relInstanceAttrs.get(refId);
          if (!existing || existing[relAttr] === undefined) {
            relInstanceAttrs.set(refId, flat);
          }
        }
      }
    }

    const allRels = store.listRelations();
    const matched = allRels.filter((r) => r.stereotypes.includes(relStereo));

    const out: RelRecord[] = [];
    let hidden = 0;
    let counter = 1;
    for (const r of matched) {
      // Vind de template-kant: source of target dat het template-(sub)type
      // matcht.
      const src = store.getConcept(r.sourceConceptId);
      const tgt = store.getConcept(r.targetConceptId);
      const template = matchesTemplate(src) ? src : matchesTemplate(tgt) ? tgt : null;
      if (!template) {
        hidden++;
        continue;
      }
      const tmplValue = template.attributes?.[tmplAttr];
      // Type-waarde: altijd van de entity (relation.attributes).
      const relValueType = r.attributes?.[relAttr];
      // Instance-waarde: alleen op niveau Instantie, uit de viewState-bag.
      const relValueInstance =
        dataLevel === "instance" ? relInstanceAttrs.get(r.id)?.[relAttr] : undefined;
      const has = (v: unknown) => v !== undefined && v !== null && v !== "";
      if (!has(tmplValue)) {
        hidden++;
        continue;
      }
      // Minstens één relatie-waarde (type of instance) nodig.
      if (!has(relValueType) && !has(relValueInstance)) {
        hidden++;
        continue;
      }
      out.push({
        relation: r,
        templateConcept: template,
        number: counter++,
        tmplValue,
        relValueType: has(relValueType) ? relValueType : undefined,
        relValueInstance: has(relValueInstance) ? relValueInstance : undefined,
      });
    }
    return {
      records: out,
      relSchema: relSchemaResolved,
      tmplSchema: tmplSchemaResolved,
      hiddenCount: hidden,
    };
  }, [revision, relStereo, relAttr, tmplStereo, tmplAttr, dataLevel]);

  // ── Build axes ──────────────────────────────────────────────────
  const tmplAxis = useMemo(
    () =>
      buildAxisFromSchema(
        tmplSchema,
        records.map((r) => r.tmplValue),
        lang,
      ),
    [tmplSchema, records, lang],
  );
  const relAxis = useMemo(
    () =>
      buildAxisFromSchema(
        relSchema,
        // Beide relatie-waarden voeden de as (type + instance), zodat de
        // numeriek-bucket-range beide dekt. Voor enum-assen maakt 't niet
        // uit (opties komen uit het schema).
        records.flatMap((r) => [r.relValueType, r.relValueInstance]),
        lang,
      ),
    [relSchema, records, lang],
  );

  // Map naar V/H assen volgens template_axis-keuze.
  const hAxis = templateAxis === "vertical" ? relAxis : tmplAxis;
  // Verticale as: waarden lopen van ONDER (eerste enum-optie / laagste
  // waarde) naar BOVEN (laatste / hoogste). De tabel rendert buckets[0]
  // bovenaan, dus keren we de buckets om. Horizontaal blijft links→rechts.
  const vAxisSource = templateAxis === "vertical" ? tmplAxis : relAxis;
  const vAxis = useMemo<AxisModel>(
    () => ({ ...vAxisSource, buckets: [...vAxisSource.buckets].reverse() }),
    [vAxisSource],
  );

  // Expandeer elke record naar 1 of 2 bolletjes (dots). Type-bolletje voor
  // de entity-waarde; instance-bolletje voor de instance-waarde — maar
  // alleen wanneer die in een ANDERE cel valt dan het type-bolletje
  // (gelijke cel → alleen type tonen, geen dubbel bolletje).
  const placed = useMemo(() => {
    const dots: PlottedRelation[] = [];
    const templateVertical = templateAxis === "vertical";
    for (const r of records) {
      // Template leeft op de gekozen template-as; de relatie op de andere.
      const tmplBucket = templateVertical
        ? bucketOf(vAxis, r.tmplValue)
        : bucketOf(hAxis, r.tmplValue);
      const mk = (relVal: unknown, kind: "type" | "instance"): PlottedRelation => {
        const relBucket = templateVertical ? bucketOf(hAxis, relVal) : bucketOf(vAxis, relVal);
        return {
          relation: r.relation,
          templateConcept: r.templateConcept,
          number: r.number,
          kind,
          vBucket: templateVertical ? tmplBucket : relBucket,
          hBucket: templateVertical ? relBucket : tmplBucket,
        };
      };
      let typeCell: { v: number; h: number } | null = null;
      if (r.relValueType !== undefined) {
        const dot = mk(r.relValueType, "type");
        dots.push(dot);
        typeCell = { v: dot.vBucket, h: dot.hBucket };
      }
      if (r.relValueInstance !== undefined) {
        const dot = mk(r.relValueInstance, "instance");
        // Alleen tonen als andere cel dan type (of geen type-dot).
        if (!typeCell || dot.vBucket !== typeCell.v || dot.hBucket !== typeCell.h) {
          dots.push(dot);
        }
      }
    }
    return dots;
  }, [records, vAxis, hAxis, templateAxis]);

  const configured =
    vAxis.mode !== "unconfigured" && hAxis.mode !== "unconfigured" && records.length >= 0;

  const name = resolveBilingual(diagram.name, lang, "nl");
  // Alleen tonen wanneer er werkelijk inhoud is — anders zou
  // resolveBilingual op een leeg {}-object "(unnamed)" teruggeven.
  const description =
    diagram.description && (diagram.description.nl || diagram.description.en)
      ? resolveBilingual(diagram.description, lang)
      : "";

  // Titel-font schaalt mee met de cel-grootte (zelfde helper als de grid-
  // labels), zodat titel + labels samen groter worden bij drukke cellen.
  const { fontScale: titleFontScale } = computeCellMetrics(placed);

  // Axis labels
  const vAxisTitle = useMemo(() => {
    const schema = templateAxis === "vertical" ? tmplSchema : relSchema;
    return schema
      ? resolveBilingual(schema.label, lang)
      : templateAxis === "vertical"
        ? tmplAttr
        : relAttr;
  }, [templateAxis, tmplSchema, relSchema, lang, tmplAttr, relAttr]);
  const hAxisTitle = useMemo(() => {
    const schema = templateAxis === "vertical" ? relSchema : tmplSchema;
    return schema
      ? resolveBilingual(schema.label, lang)
      : templateAxis === "vertical"
        ? relAttr
        : tmplAttr;
  }, [templateAxis, tmplSchema, relSchema, lang, tmplAttr, relAttr]);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: eigen canvas-cel; keyboard-a11y hoort op canvas-grid-niveau (nog te doen), niet per cel
    <div
      className="flex h-full w-full flex-col overflow-hidden bg-white"
      onDoubleClick={(e) => {
        if (e.defaultPrevented) return;
        const ui = useUi.getState();
        ui.selectDiagram(diagram.id);
        ui.setAttributePanelOpen(true);
      }}
    >
      <div className="relative flex flex-1 overflow-hidden">
        {/* biome-ignore lint/a11y/noStaticElementInteractions: eigen canvas-cel; keyboard-a11y hoort op canvas-grid-niveau (nog te doen), niet per cel */}
        <div
          ref={scrollRef}
          onMouseEnter={() => {
            hovered.current = true;
          }}
          onMouseLeave={() => {
            hovered.current = false;
          }}
          className="flex flex-1 items-center justify-center overflow-hidden p-6"
        >
          {!relStereo || !tmplStereo ? (
            <div className="max-w-md rounded-md border border-slate-300 bg-slate-50 p-6 text-center text-slate-600 text-sm">
              <div className="mb-2 font-medium text-slate-800">
                {t("heatMap.relation.notConfiguredTitle")}
              </div>
              <div>{t("heatMap.relation.notConfiguredBody")}</div>
            </div>
          ) : !configured ? (
            <div className="max-w-md rounded-md border border-slate-300 bg-slate-50 p-6 text-center text-slate-600 text-sm">
              <div className="mb-2 font-medium text-slate-800">
                {t("heatMap.relation.notEnumTitle")}
              </div>
              <div>{t("heatMap.relation.notEnumBody")}</div>
            </div>
          ) : (
            <div
              className="flex flex-col items-center gap-3"
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: "center center",
              }}
            >
              {/* Diagram-titel: gecentreerd boven het grid, groter dan de
                  as-/cel-tekst, en meezoomend. Dubbelklik opent het
                  AttributePanel voor naam-bewerking. */}
              <div className="text-center">
                {/* biome-ignore lint/a11y/noStaticElementInteractions: eigen canvas-cel; keyboard-a11y hoort op canvas-grid-niveau (nog te doen), niet per cel */}
                <div
                  className="inline-block cursor-pointer font-semibold text-slate-800 hover:text-blue-600"
                  style={{ fontSize: `${1.5 * titleFontScale}rem` }}
                  title={t("heatMap.editNameTitle")}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    const ui = useUi.getState();
                    ui.selectDiagram(diagram.id);
                    ui.setAttributePanelOpen(true);
                  }}
                >
                  {name}
                </div>
                {description && (
                  <div
                    className="mt-1 text-slate-500"
                    style={{ fontSize: `${0.875 * titleFontScale}rem` }}
                  >
                    {description}
                  </div>
                )}
              </div>
              <SimpleGrid
                plotted={placed}
                vAxis={vAxis}
                hAxis={hAxis}
                vAxisTitle={vAxisTitle}
                hAxisTitle={hAxisTitle}
                lang={lang}
                showDiagonal={showDiagonal}
                hiddenCount={hiddenCount}
                applyColor={applyColor}
                colorStart={colorStart}
                colorEnd={colorEnd}
                colorDirection={colorDirection}
                dotFillOverride={dotFillOverride}
                dotTextOverride={dotTextOverride}
                dotFillInstance={dotFillInstance}
                dotTextInstance={dotTextInstance}
              />
            </div>
          )}
        </div>
        {relStereo && tmplStereo && configured && (
          <div
            className={`absolute flex items-center gap-1 rounded-md border border-slate-300 bg-white px-1 py-1 shadow-sm ${toolbarPosClass[toolbarPos]}`}
          >
            <button
              type="button"
              className="rounded px-1.5 py-0.5 text-slate-500 text-xs hover:bg-slate-100"
              title={t("heatMap.toolbar.move")}
              onClick={() => {
                const order: (typeof toolbarPos)[] = [
                  "top-left",
                  "top-right",
                  "bottom-right",
                  "bottom-left",
                ];
                setToolbarPos(
                  order[(order.indexOf(toolbarPos) + 1) % order.length] ?? "bottom-right",
                );
              }}
            >
              ⤵
            </button>
            <span className="h-4 w-px bg-slate-200" />
            <button
              type="button"
              className="rounded px-2 py-0.5 text-slate-600 text-sm hover:bg-slate-100"
              title={t("heatMap.toolbar.zoomOut")}
              onClick={() => setZoom((z) => clamp(z / 1.1, 0.3, 4))}
            >
              −
            </button>
            <button
              type="button"
              className="rounded px-2 py-0.5 text-slate-600 text-xs hover:bg-slate-100"
              title={t("heatMap.toolbar.resetZoom")}
              onClick={() => setZoom(1)}
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              type="button"
              className="rounded px-2 py-0.5 text-slate-600 text-sm hover:bg-slate-100"
              title={t("heatMap.toolbar.zoomIn")}
              onClick={() => setZoom((z) => clamp(z * 1.1, 0.3, 4))}
            >
              +
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

// ── Helpers ─────────────────────────────────────────────────────────

function buildAxisFromSchema(
  schema: AttributeSchema | undefined,
  values: unknown[],
  lang: string,
): AxisModel {
  if (!schema) return { mode: "unconfigured", attrName: "", buckets: [] };
  const baseType = schema.type.endsWith("?") ? schema.type.slice(0, -1) : schema.type;
  if (baseType === "enum") {
    const options = (schema.options ?? []) as readonly string[];
    return {
      mode: "enum",
      attrName: schema.name,
      buckets: options.map((opt) => ({
        // Meertalig label: pack-declared option_labels (bilingual) via
        // resolveBilingual; anders de rauwe waarde met underscores als
        // spaties (bv. "partially_effective" → "partially effective").
        label: schema.option_labels?.[opt]
          ? resolveBilingual(schema.option_labels[opt], lang, opt)
          : opt.replace(/_/g, " "),
        enumValue: opt,
      })),
      enumOptions: options,
    };
  }
  if (baseType === "int" || baseType === "float" || baseType === "number") {
    const nums = values
      .map((v) => (typeof v === "number" ? v : Number.parseFloat(String(v))))
      .filter((v): v is number => !Number.isNaN(v));
    if (nums.length === 0) {
      return { mode: "unconfigured", attrName: schema.name, buckets: [] };
    }
    const min = Math.min(...nums);
    const max = Math.max(...nums);
    const buckets = niceBuckets(min, max, 5);
    return { mode: "numeric", attrName: schema.name, buckets };
  }
  return { mode: "unconfigured", attrName: schema.name, buckets: [] };
}

function niceBuckets(
  min: number,
  max: number,
  approxCount: number,
): { label: string; min: number; max: number }[] {
  if (max <= min) {
    return [{ label: String(min), min, max: min }];
  }
  const span = max - min;
  const rawStep = span / approxCount;
  const mag = 10 ** Math.floor(Math.log10(rawStep));
  const normalized = rawStep / mag;
  const step = (normalized < 1.5 ? 1 : normalized < 3.5 ? 2 : normalized < 7.5 ? 5 : 10) * mag;
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  const out: { label: string; min: number; max: number }[] = [];
  for (let v = lo; v < hi; v += step) {
    out.push({ label: `${formatNum(v)}–${formatNum(v + step)}`, min: v, max: v + step });
  }
  return out;
}

function formatNum(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2);
}

// Celafmeting + bijbehorende font-schaal. Single source of truth zodat
// zowel de grid-labels als de (in de parent gerenderde) titel exact
// hetzelfde meeschalen wanneer de cel groter wordt door meer bolletjes.
const DOT_SIZE = 24;
const DOT_GAP = 4;
const CELL_PADDING = 6;
const MIN_DIM = 80;
function computeCellMetrics(plotted: PlottedRelation[]): { cellSize: number; fontScale: number } {
  let maxPerCell = 0;
  const counts = new Map<string, number>();
  for (const p of plotted) {
    if (p.vBucket < 0 || p.hBucket < 0) continue;
    const k = `${p.vBucket},${p.hBucket}`;
    const n = (counts.get(k) ?? 0) + 1;
    counts.set(k, n);
    if (n > maxPerCell) maxPerCell = n;
  }
  const { maxR } = dotPositions(maxPerCell, DOT_SIZE, DOT_GAP);
  const cellSize = Math.max(MIN_DIM, maxR * 2 + DOT_SIZE + CELL_PADDING * 2);
  // Font groeit lineair mee met de cel t.o.v. de minimumcel, gedempt en
  // gecapt zodat 't bij extreem drukke cellen niet uit de hand loopt.
  const fontScale = Math.min(2.5, Math.max(1, 1 + (cellSize / MIN_DIM - 1) * 0.7));
  return { cellSize, fontScale };
}

function bucketOf(axis: AxisModel, value: unknown): number {
  if (axis.mode === "enum") {
    const s = typeof value === "string" ? value : String(value ?? "");
    return axis.buckets.findIndex((b) => b.enumValue === s);
  }
  if (axis.mode === "numeric") {
    const n = typeof value === "number" ? value : Number.parseFloat(String(value));
    if (Number.isNaN(n)) return -1;
    return axis.buckets.findIndex(
      (b) => n >= (b.min ?? Number.NEGATIVE_INFINITY) && n < (b.max ?? Number.POSITIVE_INFINITY),
    );
  }
  return -1;
}

// ── Grid rendering ──────────────────────────────────────────────────

interface SimpleGridProps {
  plotted: PlottedRelation[];
  vAxis: AxisModel;
  hAxis: AxisModel;
  vAxisTitle: string;
  hAxisTitle: string;
  lang: string;
  showDiagonal: boolean;
  hiddenCount: number;
  applyColor: boolean;
  colorStart: string;
  colorEnd: string;
  colorDirection: "horizontal" | "vertical" | "both";
  dotFillOverride?: string | undefined;
  dotTextOverride?: string | undefined;
  dotFillInstance: string;
  dotTextInstance?: string | undefined;
}

function SimpleGrid({
  plotted,
  vAxis,
  hAxis,
  vAxisTitle,
  hAxisTitle,
  lang,
  showDiagonal,
  hiddenCount,
  applyColor,
  colorStart,
  colorEnd,
  colorDirection,
  dotFillOverride,
  dotTextOverride,
  dotFillInstance,
  dotTextInstance,
}: SimpleGridProps) {
  const { t } = useTranslation();
  const rows = vAxis.buckets.length || 1;
  const cols = hAxis.buckets.length || 1;
  // Group datapoints by cell.
  const byCell = useMemo(() => {
    const m = new Map<string, PlottedRelation[]>();
    for (const p of plotted) {
      if (p.vBucket < 0 || p.hBucket < 0) continue;
      const key = `${p.vBucket},${p.hBucket}`;
      const arr = m.get(key);
      if (arr) arr.push(p);
      else m.set(key, [p]);
    }
    return m;
  }, [plotted]);

  // Border-kleur: bij gekleurde cellen wit zodat de gradient niet
  // onderbroken wordt; anders slate-300.
  const cellBorder = applyColor ? "#ffffff" : "#cbd5e1";

  // Cel-grootte + font-schaal via gedeelde helper (zodat labels én titel
  // identiek meeschalen). Cel groeit mee met de drukst-bezette cel zodat
  // de radiaal geplaatste bolletjes nooit overlappen.
  const { cellSize, fontScale } = computeCellMetrics(plotted);
  // Font-sizes meeschalend met de cel (basis: text-xs = 0.75rem).
  const labelFont = `${0.75 * fontScale}rem`;

  // Werkelijke tabelhoogte (incl. as-labels onderaan) meten zodat de
  // legenda-kolom precies tot de onderkant van het diagram doorloopt en
  // dan pas naar een nieuwe kolom wrapt. offsetHeight negeert de
  // zoom-transform (layout-hoogte). Fallback op de schatting tot gemeten.
  const tableRef = useRef<HTMLTableElement>(null);
  const [tableHeight, setTableHeight] = useState(0);
  useLayoutEffect(() => {
    if (tableRef.current) setTableHeight(tableRef.current.offsetHeight);
  }, []);
  // Re-meet wanneer grid-dimensies veranderen.
  useLayoutEffect(() => {
    if (tableRef.current) setTableHeight(tableRef.current.offsetHeight);
  }, [rows, cols, cellSize, plotted.length]);
  const legendHeight = tableHeight > 0 ? tableHeight : cellSize * rows;

  // Cel-gradient (wanneer "Kleur toepassen" aan). Richting via
  // color_direction: horizontaal (links→rechts), verticaal (onder→boven)
  // of beide (diagonaal links-onder→rechts-boven). Bij uit: neutraal.
  // Identiek aan HeatMapCanvasV2.
  const cellBg = (rowIdx: number, colIdx: number): string | undefined => {
    if (!applyColor) return undefined;
    let t: number;
    if (colorDirection === "horizontal") {
      t = cols <= 1 ? 0 : colIdx / (cols - 1);
    } else if (colorDirection === "vertical") {
      t = rows <= 1 ? 0 : (rows - 1 - rowIdx) / (rows - 1);
    } else {
      const denom = Math.max(1, rows + cols - 2);
      t = (rows - 1 - rowIdx + colIdx) / denom;
    }
    return lerpColor(colorStart, colorEnd, t);
  };

  return (
    <div className="flex flex-col items-center gap-2">
      {/* Verticale as-titel links (geroteerd) + tabel — zelfde structuur
          als HeatMapCanvasV2: de horizontale as-waarden + titel staan
          ONDER het grid (tfoot). Legenda staat rechts ernaast. */}
      <div className="flex items-start gap-6">
        <div className="flex items-stretch">
          <div className="flex items-center pr-1">
            <div
              className="whitespace-nowrap font-semibold text-slate-600 uppercase tracking-wide"
              style={{
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
                fontSize: labelFont,
              }}
            >
              {vAxisTitle}
            </div>
          </div>
          <table ref={tableRef} className="border-collapse">
            <tbody>
              {vAxis.buckets.map((bv, vi) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: positionele grid-cel; het grid wordt volledig herberekend zonder reordering, dus de index is een stabiele identiteit
                <tr key={`v${vi}`}>
                  <th
                    className="px-3 py-1 text-right align-middle font-medium text-slate-700"
                    style={{ fontSize: labelFont }}
                  >
                    {bv.label}
                  </th>
                  {hAxis.buckets.map((_, hi) => {
                    const inCell = byCell.get(`${vi},${hi}`) ?? [];
                    const isDiagonal =
                      showDiagonal && rows === cols && (vi === hi || vi + hi === rows - 1);
                    const bg = cellBg(vi, hi);
                    const { positions } = dotPositions(inCell.length, DOT_SIZE, DOT_GAP);
                    return (
                      <td
                        // biome-ignore lint/suspicious/noArrayIndexKey: positionele grid-cel; het grid wordt volledig herberekend zonder reordering, dus de index is een stabiele identiteit
                        key={`v${vi}h${hi}`}
                        className="p-0 align-middle"
                        style={{
                          width: cellSize,
                          height: cellSize,
                          backgroundColor:
                            bg ??
                            (isDiagonal
                              ? "rgba(59, 130, 246, 0.08)"
                              : applyColor
                                ? "#f8fafc"
                                : "transparent"),
                          border: `1px solid ${isDiagonal && !bg ? "rgba(59, 130, 246, 0.45)" : cellBorder}`,
                        }}
                      >
                        <div className="relative" style={{ width: cellSize, height: cellSize }}>
                          {inCell.map((p, dotIdx) => {
                            const pos = positions[dotIdx] ?? { x: 0, y: 0 };
                            return (
                              <div
                                key={`${p.relation.id}-${p.kind}`}
                                style={{
                                  position: "absolute",
                                  left: cellSize / 2 + pos.x - DOT_SIZE / 2,
                                  top: cellSize / 2 + pos.y - DOT_SIZE / 2,
                                }}
                              >
                                <DeploymentDot
                                  datapoint={p}
                                  lang={lang}
                                  size={DOT_SIZE}
                                  applyColor={applyColor}
                                  fillOverride={dotFillOverride}
                                  textOverride={dotTextOverride}
                                  fillInstance={dotFillInstance}
                                  textInstance={dotTextInstance}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            <tfoot>
              {/* Horizontale as-waarden onder het grid + as-titel onderaan,
                net als de objecten-heatmap. */}
              <tr>
                <th />
                {hAxis.buckets.map((b, i) => (
                  <th
                    // biome-ignore lint/suspicious/noArrayIndexKey: positionele grid-cel; het grid wordt volledig herberekend zonder reordering, dus de index is een stabiele identiteit
                    key={`hb${i}`}
                    className="px-2 py-1 text-center font-medium text-slate-700"
                    style={{ width: cellSize, fontSize: labelFont }}
                  >
                    {b.label}
                  </th>
                ))}
              </tr>
              <tr>
                <th />
                <th
                  colSpan={hAxis.buckets.length}
                  className="pt-1 text-center font-semibold text-slate-600 uppercase tracking-wide"
                  style={{ fontSize: labelFont }}
                >
                  {hAxisTitle}
                </th>
              </tr>
            </tfoot>
          </table>
        </div>
        <DeploymentLegend
          plotted={plotted}
          lang={lang}
          matrixHeight={legendHeight}
          applyColor={applyColor}
          fillOverride={dotFillOverride}
          textOverride={dotTextOverride}
          fillInstance={dotFillInstance}
          textInstance={dotTextInstance}
        />
      </div>
      <div className="mt-1 flex items-center gap-4 text-slate-500 text-xs">
        <span>
          {
            new Set(
              plotted.filter((p) => p.vBucket >= 0 && p.hBucket >= 0).map((p) => p.relation.id),
            ).size
          }{" "}
          {t("heatMap.relation.plotted")}
        </span>
        {hiddenCount > 0 && (
          <span className="text-amber-600">
            {hiddenCount} {t("heatMap.relation.hidden")}
          </span>
        )}
        {showDiagonal && (
          <span className="flex items-center gap-1">
            <span className="inline-block h-3 w-3 border-blue-300 border-2 border-dashed bg-blue-50" />
            {t("heatMap.relation.diagonal")}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Legenda rechts van het grid — genummerde bolletjes met naam, zelfde
 * patroon als ConceptLegend in HeatMapCanvasV2: chunk in kolommen op
 * basis van de matrix-hoogte zodat lange lijsten naast elkaar staan i.p.v.
 * onder het grid uit te lopen. Naam = template-concept — relatie-label.
 */
function DeploymentLegend({
  plotted,
  lang,
  matrixHeight,
  applyColor,
  fillOverride,
  textOverride,
  fillInstance,
  textInstance,
}: {
  plotted: PlottedRelation[];
  lang: string;
  matrixHeight: number;
  applyColor: boolean;
  fillOverride?: string | undefined;
  textOverride?: string | undefined;
  fillInstance: string;
  textInstance?: string | undefined;
}) {
  // Groepeer per relatie (gedeeld nummer): één legenda-regel per relatie
  // met beide bolletjes (type, dan instance) naast elkaar. Alleen in-cel
  // geplotte dots tellen mee.
  const groups = useMemo(() => {
    const byRel = new Map<
      string,
      { type?: PlottedRelation; instance?: PlottedRelation; number: number; rel: PlottedRelation }
    >();
    for (const p of plotted) {
      if (p.vBucket < 0 || p.hBucket < 0) continue;
      const g = byRel.get(p.relation.id) ?? { number: p.number, rel: p };
      if (p.kind === "type") g.type = p;
      else g.instance = p;
      byRel.set(p.relation.id, g);
    }
    return [...byRel.values()].sort((a, b) => a.number - b.number);
  }, [plotted]);
  if (groups.length === 0) return null;
  const ROW_HEIGHT = 32;
  const rowsPerCol = Math.max(1, Math.floor(matrixHeight / ROW_HEIGHT));
  const columns: (typeof groups)[] = [];
  for (let i = 0; i < groups.length; i += rowsPerCol) {
    columns.push(groups.slice(i, i + rowsPerCol));
  }
  return (
    <div className="flex shrink-0 gap-4">
      {columns.map((col, ci) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: positionele grid-cel; het grid wordt volledig herberekend zonder reordering, dus de index is een stabiele identiteit
        <table key={ci} className="border-collapse">
          <tbody>
            {col.map((g) => {
              const dot = g.type ?? g.instance!;
              // Alleen de objectnaam (template-concept) — relatie-label
              // bewust weggelaten.
              const text = resolveBilingual(dot.templateConcept.name, lang, "?");
              return (
                <tr key={dot.relation.id} className="hover:bg-slate-50">
                  <td className="pr-2 align-middle">
                    <div className="flex items-center gap-1">
                      {g.type && (
                        <DeploymentDot
                          datapoint={g.type}
                          lang={lang}
                          applyColor={applyColor}
                          fillOverride={fillOverride}
                          textOverride={textOverride}
                          fillInstance={fillInstance}
                          textInstance={textInstance}
                        />
                      )}
                      {g.instance && (
                        <DeploymentDot
                          datapoint={g.instance}
                          lang={lang}
                          applyColor={applyColor}
                          fillOverride={fillOverride}
                          textOverride={textOverride}
                          fillInstance={fillInstance}
                          textInstance={textInstance}
                        />
                      )}
                    </div>
                  </td>
                  <td
                    className="min-w-[140px] max-w-[240px] cursor-pointer py-1 align-middle text-slate-700 text-xs leading-tight"
                    onClick={() => {
                      const ui = useUi.getState();
                      ui.selectRelation(dot.relation.id);
                      ui.setAttributePanelOpen(true);
                    }}
                  >
                    {text}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ))}
    </div>
  );
}

function DeploymentDot({
  datapoint,
  lang,
  size = 20,
  applyColor,
  fillOverride,
  textOverride,
  fillInstance,
  textInstance,
}: {
  datapoint: PlottedRelation;
  lang: string;
  size?: number;
  applyColor: boolean;
  fillOverride?: string | undefined;
  textOverride?: string | undefined;
  fillInstance: string;
  textInstance?: string | undefined;
}) {
  // Titel = alleen de objectnaam (template-concept); relatie-label
  // bewust weggelaten.
  const title = resolveBilingual(datapoint.templateConcept.name, lang, "?");
  // Instance-bolletje gebruikt z'n eigen kleur; type-bolletje de primaire.
  const bg =
    datapoint.kind === "instance"
      ? fillInstance
      : (fillOverride ?? (applyColor ? "#ffffff" : "#3b82f6"));
  const border = darken(bg, 0.2);
  const textColor =
    datapoint.kind === "instance"
      ? (textInstance ?? contrastText(bg))
      : (textOverride ?? contrastText(bg));
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: eigen canvas-cel; keyboard-a11y hoort op canvas-grid-niveau (nog te doen), niet per cel
    <span
      className="inline-flex cursor-pointer items-center justify-center rounded-full border font-semibold shadow-sm transition hover:brightness-95"
      style={{
        backgroundColor: bg,
        borderColor: border,
        color: textColor,
        width: size,
        height: size,
        fontSize: size <= 20 ? 10 : 11,
        lineHeight: 1,
      }}
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        const ui = useUi.getState();
        ui.selectRelation(datapoint.relation.id);
        ui.setAttributePanelOpen(true);
      }}
    >
      {datapoint.number}
    </span>
  );
}

// ── Kleur-helpers (kopie van HeatMapCanvasV2 zodat beide heatmaps
//    dezelfde gradient/contrast-logica delen) ─────────────────────────

function lerpColor(a: string, b: string, t: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  if (!ca || !cb) return a;
  const r = Math.round(ca.r + (cb.r - ca.r) * t);
  const g = Math.round(ca.g + (cb.g - ca.g) * t);
  const blue = Math.round(ca.b + (cb.b - ca.b) * t);
  return `rgb(${r}, ${g}, ${blue})`;
}

function parseHex(s: string): { r: number; g: number; b: number } | null {
  const m = /^#([0-9A-Fa-f]{6})$/.exec(s);
  if (!m) {
    const rgb = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/.exec(s);
    if (rgb) return { r: +rgb[1]!, g: +rgb[2]!, b: +rgb[3]! };
    return null;
  }
  const hex = m[1]!;
  return {
    r: Number.parseInt(hex.slice(0, 2), 16),
    g: Number.parseInt(hex.slice(2, 4), 16),
    b: Number.parseInt(hex.slice(4, 6), 16),
  };
}

function darken(color: string, amount: number): string {
  const c = parseHex(color);
  if (!c) return color;
  return `rgb(${Math.round(c.r * (1 - amount))}, ${Math.round(c.g * (1 - amount))}, ${Math.round(c.b * (1 - amount))})`;
}

/** WCAG-style contrast pick — wit op donker, donker op licht. */
function contrastText(bg: string): string {
  const c = parseHex(bg);
  if (!c) return "#111827";
  const lum = (0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b) / 255;
  return lum > 0.6 ? "#111827" : "#ffffff";
}
